import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Control, FieldValues, useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useCreateProgramInvoiceMutation } from "../../../../../../../api/services/client/clientApis";
import { ProgramExpenseData } from "../../../../../../../api/services/program/models";
import { ReactComponent as DownloadIcon } from "../../../../../../../assets/images/download.svg";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonTertiary,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiLink,
  MuiModal,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  RowProps,
  WarningConfirmationModal,
} from "../../../../../../../components";
import { MuiDoubleLabelHorizontal } from "../../../../../../../components/doubleLabel/MuiDoubleLabelHorizontal";
import PermissionWrapper from "../../../../../../../components/permissionWrapper/PermissionWrapper";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../../components/statusBadge/StatusBadge";
import { MuiNotesToolTip } from "../../../../../../../components/tooltip/notesToolTip/MuiNotesToolTip";
import {
  CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
  DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL,
  DELIVERABLE_FRAMEWORK_TYPE,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../helpers/constants/countryDateFormat";
import {
  KeyValue,
  TableActionType,
  TableActions,
} from "../../../../../../../helpers/types/common";
import {
  buildActionMenuItems,
  checkWithLowerCase,
  convertToUserFormat,
  convertToUTCFormat,
  filterListByFilterKeyValue,
  getAmountWithCurrency,
  isEmpty,
  isNotEmpty,
  isStatusClosed,
  replaceHashWithValue,
} from "../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../../redux/slices/appState/appStateSlice";
import { programStatusSelector } from "../../../../../../../redux/slices/program/programSelector";
import { updateOpcoFieldsEditable } from "../../../../../../../redux/slices/program/programSlice";
import { useDownloadInvoiceToExcel } from "../../../../../hooks/useDownloadInvoiceToExcel";
import {
  ADD_PROGRAM_FEE_AND_EXPENSE_TYPES,
  FeesAndExpenseRow,
  ProgramDetail,
  ProgramFessAndExpensesColumnKey,
} from "../../../../../modelsAndSchemas/Models";
import {
  addFeesAndExpensesSiteCoreKeys,
  feesAndExpensesSiteCoreKeys,
  invoiceHistorySiteCoreKeys,
  raiseInvoiceSiteCoreKeys,
} from "../../../../../siteCore/SiteCoreKeys";
import { InvoiceHistory } from "../../../../invoiceHistory/InvoiceHistoryMain";
import { ViewProgramFeeModal } from "../../../../programAndCandidate/tabs/feesAndExpense/components/viewProgramFeeModal/ViewProgramFeeModal";
import { RaiseInvoice } from "./raiseInvoice/RaiseInvoiceModal";
import ViewFeeExpenseModal from "./viewFeesAndExpense/ViewFeeExpenseModal";
import { isProgramFeeType } from "../../../../../helper";

const pageId = `${PAGE_IDS.PROGRAM_TEMPLATE}-fees-and-expense-list`;

const SearchFormControl = ({
  name,
  control,
  placeholder,
  requiredARIALabel,
}: {
  name: string;
  control: Control<FieldValues, any>;
  placeholder: string;
  requiredARIALabel: string;
}) => (
  <FormController control={control} name={name} key={name}>
    <MuiTextField
      id={`${pageId}-${name}`}
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      }
      maxLength={MAX_LENGTH_50}
      placeholder={placeholder}
      showCloseIcon
      shouldTrimSpaces={false}
      requiredARIALabel={requiredARIALabel}
    />
  </FormController>
);

interface FeesAndExpenseListProps {
  expenseList: ProgramExpenseData[];
  isAddButtonVisible: boolean;
  handleAddFeesAndExpense: () => void;
  onActionClick: (
    selectedInvoice: ProgramExpenseData,
    actionName: TableActionType,
  ) => void;
  onRefetchApi: (programId: string) => void;
  programId: string;
  poNumber?: string;
  isViewOnly?: boolean;
  billingCurrencyName: string;
  isEditProgram?: boolean;
  programDetail?: ProgramDetail;
}

export const FeesAndExpenseList = ({
  onActionClick,
  expenseList,
  isAddButtonVisible,
  handleAddFeesAndExpense,
  onRefetchApi,
  programId,
  isViewOnly = false,
  billingCurrencyName,
  poNumber,
  isEditProgram,
  programDetail,
}: FeesAndExpenseListProps) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const getLabel = useFetchLabel(feesAndExpensesSiteCoreKeys.feesAndExpenses);
  const { control } = useForm<Partial<KeyValue>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const isSiteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const isProgramTagWithProject = !!programDetail?.isProjectTagged;
  const programStatusId = useSelector(programStatusSelector);
  const isProgramStatusClosed = useMemo(
    () => isStatusClosed(programStatusId as StatusBadgeType),
    [programStatusId],
  );
  const [state, setState] = useState<{
    filteredList: FeesAndExpenseRow[];
    originalList: FeesAndExpenseRow[];
    categoryOptions: OptionsDataType[];
    feeOrExpenseTypeOptions: OptionsDataType[];
    typeIdOptions: OptionsDataType[];
    totalBillingAmount: number;
    totalTax: number;
    showRaiseInvoiceModal: boolean;
  }>({
    filteredList: [],
    originalList: [],
    categoryOptions: [],
    typeIdOptions: [],
    feeOrExpenseTypeOptions: [],
    totalBillingAmount: 0,
    totalTax: 0,
    showRaiseInvoiceModal: false,
  });
  const [showRaiseInvoiceModal, setShowRaiseInvoiceModal] = useState(false);
  const [showDossierErrorModal, setShowDossierErrorModal] = useState(false);
  const [showInvoiceHistoryModal, setShowInvoiceHistoryModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [invoiceId, setInvoiceId] = useState<string>("");
  const [selectedPONumber, setSelectedPONumber] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState<string>("");
  const [paymentTerms, setPaymentTerms] = useState<string>("");
  const [localInvoiceNumber, setLocalInvoiceNumber] = useState<string>("");
  const isEditWithPermission =
    !isViewOnly &&
    hasModulePermission(MODULE_PERMISSIONS.PROGRAM_ADD_INVOICE_GENERATION);

  const dispatch = useDispatch();

  const {
    mutate: raiseInvoice,
    reset: resetRaiseInvoice,
    isPending: isRaiseInvoiceLoading,
    data: raiseInvoiceData,
    isError: isRaiseInvoiceError,
    error: raiseInvoiceError,
  } = useCreateProgramInvoiceMutation();

  const isClientTypeNCBP = useMemo(
    () =>
      checkWithLowerCase(
        programDetail?.clientType,
        CLIENT_TYPE_NON_CLIENT_BILLING_PARTY_SITE_CORE_KEY_VALUE,
      ),
    [programDetail?.clientType],
  );

  useEffect(() => {
    if (raiseInvoiceData) {
      setShowConfirmation(false);
      setInvoiceNumber(raiseInvoiceData.number);
      setInvoiceId(raiseInvoiceData.id);
      setShowSuccess(true);
      if (isClientTypeNCBP) {
        dispatch(updateOpcoFieldsEditable(false));
      }
    }
  }, [raiseInvoiceData]);

  useEffect(() => {
    if (isRaiseInvoiceLoading) {
      dispatch(
        toggleLoader({
          isVisible: isRaiseInvoiceLoading,
        }),
      );
      return () => {
        dispatch(
          toggleLoader({
            isVisible: false,
          }),
        );
      };
    }
  }, [isRaiseInvoiceLoading]);

  useEffect(() => {
    if (isRaiseInvoiceError) {
      setShowConfirmation(false);
    }
  }, [isRaiseInvoiceError]);

  // When user clicks primary button in RaiseInvoice, show confirmation modal
  const handlePrimaryButtonClick = () => {
    setShowConfirmation(true); // Open confirmation modal
  };

  const handleConfirm = () => {
    const checkedItems = state.filteredList.filter((item) => item.isChecked);
    const payload = {
      localInvoiceNumber,
      programExpenses: checkedItems.map((item, index) => {
        return {
          expenseId: item.id,
          lineNumber: index + 1,
        };
      }),
      purchaseOrderId: selectedPONumber ?? "",
      feeOrExpenseAmount: parseFloat(
        (state.totalBillingAmount - state.totalTax).toFixed(2),
      ),
      totalTaxAmount: parseFloat(state.totalTax.toFixed(2)),
      totalAmount: parseFloat(state.totalBillingAmount.toFixed(2)),
      invoiceDate: convertToUTCFormat(invoiceDate, userCountryDateFormat),
      paymentTermId: paymentTerms,
    };

    raiseInvoice({
      programId: programId,
      payload: payload,
    });
  };

  const handleInvoiceButtonClick = () => {
    setShowInvoiceHistoryModal(false);
    onRefetchApi(programId); // This triggers the parent API refetch
  };
  const handleSuccessClose = () => {
    onRefetchApi(programId);
    setShowRaiseInvoiceModal(false);
    setShowSuccess(false);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };
  const [showProgramFeeViewModal, setShowProgramFeeViewModal] = useState(false);
  const [showViewFeesAndExpenseModal, setShowViewFeesAndExpenseModal] =
    useState(false);

  const [selectedProgramExpenseId, setSelectedProgramExpenseId] =
    useState<string>("");

  const handleOnViewClick = (row: FeesAndExpenseRow) => {
    switch (row?.typeId) {
      case ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_FEE: {
        setShowProgramFeeViewModal(true);
        break;
      }
      case ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_EXPENSE:
      case ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_OR_PROJECT_FEE: {
        setSelectedProgramExpenseId(row.id);
        setShowViewFeesAndExpenseModal(true);
        break;
      }
    }
  };

  const getAddFeesExpenseLabel = (key: string) => {
    return fetchLabel(addFeesAndExpensesSiteCoreKeys.addFeesAndExpenses, key);
  };

  const getProgramFeeLabel = () => {
    return getAddFeesExpenseLabel(
      addFeesAndExpensesSiteCoreKeys.programFeeType,
    );
  };

  const getStatusValueByKeyOfDropdown = (statusId: string) => {
    return fetchValueByKeyOfDropdown(
      feesAndExpensesSiteCoreKeys.feesAndExpenses,
      feesAndExpensesSiteCoreKeys.itemStatusList,
      statusId,
    );
  };

  // extract: check if expense should be marked checked
  const isExpenseChecked = (
    expense: ProgramExpenseData,
    prevCheckedList: FeesAndExpenseRow[],
  ) => {
    if (expense.statusId !== STATUS_BADGE_TYPE.unBilled) return false;
    return !!prevCheckedList.find((item) => item.id === expense.id)?.isChecked;
  };

  const collectOptions = (
    expense: ProgramExpenseData,
    categoryOptions: Set<string>,
    typeOptions: Set<string>,
    feeOrExpenseTypeOptions: Set<string>,
  ) => {
    if (expense["expenseCategoryId"]?.name) {
      categoryOptions.add(expense["expenseCategoryId"].name);
    }
    if (expense?.expenseTypeId?.name || expense["feeType"]?.name) {
      feeOrExpenseTypeOptions.add(
        expense["expenseTypeId"]?.name ?? expense["feeType"]?.name ?? "",
      );
    }
    if (expense?.typeId) {
      typeOptions.add(getAddFeesExpenseLabel(expense["typeId"]));
    }
  };

  const buildExpenseRow = (
    expense: ProgramExpenseData,
    prevCheckedList: FeesAndExpenseRow[],
  ) => ({
    ...expense,
    status: getStatusValueByKeyOfDropdown(expense.statusId),
    transactionDate: convertToUserFormat(
      expense.transactionDate,
      userCountryDateFormat,
    ),
    typeName: getAddFeesExpenseLabel(expense.typeId),
    name: isProgramFeeType(expense.typeId)
      ? getProgramFeeLabel()
      : expense?.name || "",
    isChecked: isExpenseChecked(expense, prevCheckedList),
    expenseCategoryName: expense["expenseCategoryId"]?.name,
    currencyName: expense["currency"]?.name,
    feeOrExpenseType:
      (expense["expenseTypeId"]?.name || expense["feeType"]?.name) ?? "",
    lineNumber: "",
  });

  // extract: map set → { label, value } array
  const toOptionArray = (set: Set<string>) =>
    [...set].map((value) => ({ label: value, value }));

  const mapFeesAndExpensesData = (data: ProgramExpenseData[]) => {
    const response: FeesAndExpenseRow[] = [];
    const categoryOptions = new Set<string>();
    const typeOptions = new Set<string>();
    const feeOrExpenseTypeOptions = new Set<string>();
    const prevCheckedList = state.originalList.filter(
      (item) => item?.isChecked && item.statusId === STATUS_BADGE_TYPE.unBilled,
    );
    data.forEach((expense: ProgramExpenseData) => {
      collectOptions(
        expense,
        categoryOptions,
        typeOptions,
        feeOrExpenseTypeOptions,
      );

      response.push(buildExpenseRow(expense, prevCheckedList));
    });

    const totalBillingAmount =
      state.originalList.length > 0
        ? getTotalCheckedAmount(response, "totalAmount")
        : 0;

    const totalTaxAmount = getTotalCheckedAmount(response, "tax");
    setState((prevState) => ({
      ...prevState,
      totalBillingAmount,
      filteredList: response,
      originalList: response,
      categoryOptions: toOptionArray(categoryOptions),
      feeOrExpenseTypeOptions: toOptionArray(feeOrExpenseTypeOptions),
      typeIdOptions: toOptionArray(typeOptions),
      totalTax: totalTaxAmount,
    }));
  };

  useEffect(() => {
    if (expenseList?.length && !isSiteCoreLoading) {
      mapFeesAndExpensesData(expenseList);
    } else {
      setState((prevState) => ({
        ...prevState,
        filteredList: [],
        originalList: [],
      }));
    }
  }, [expenseList, isSiteCoreLoading]);

  const getTotalInvoiceAmount = () => {
    if (isEmpty(expenseList)) {
      return `${billingCurrencyName} ${DEFAULT_ZERO_VALUE_WITH_TWO_DECIMAL}`;
    }
    const billedList = expenseList.filter(
      (item) => item.statusId === STATUS_BADGE_TYPE.billed,
    );
    const invoiceAmount = billedList.reduce((total, item) => {
      return total + (item.totalAmount || 0);
    }, 0);

    return `${billingCurrencyName} ${invoiceAmount.toFixed(2)}`;
  };

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(state.originalList);
  }, [filters]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const appliedFilters = (ProgramExpenseData: FeesAndExpenseRow[]) => {
    if (!ProgramExpenseData.length) return;
    const filterList = filterListByFilterKeyValue(state.originalList, filters);
    setState((prevState) => ({
      ...prevState,
      filteredList: filterList as unknown as FeesAndExpenseRow[],
    }));
    resetPagination();
  };

  const emptyMessageLabel = getLabel(feesAndExpensesSiteCoreKeys.noDataMessage);

  const isAllSelected = useMemo(() => {
    const unBilledList = state.originalList?.filter(
      (item) => item.statusId === STATUS_BADGE_TYPE.unBilled,
    );
    if (unBilledList.length === 0) return false;
    return unBilledList.every((item) => item.isChecked);
  }, [state.originalList]);

  const onChangeSelectAll = (checked: boolean) => {
    let totalBillingAmount = 0;
    const updatedList = state.filteredList.map((item) => {
      return {
        ...item,
        isChecked:
          item?.statusId === STATUS_BADGE_TYPE.unBilled ? checked : false,
      };
    });

    const updatedOriginalList = state.originalList.map((item) => ({
      ...item,
      isChecked:
        item?.statusId === STATUS_BADGE_TYPE.unBilled ? checked : false,
    }));

    totalBillingAmount = getTotalCheckedAmount(updatedList, "totalAmount");
    const totalTaxAmount = getTotalCheckedAmount(updatedList, "tax");

    setState((prevState) => ({
      ...prevState,
      originalList: updatedOriginalList,
      filteredList: updatedList,
      totalBillingAmount,
      totalTax: totalTaxAmount,
    }));
  };

  const raiseInvoiceLabel = useMemo(() => {
    const siteCoreLabel = getLabel(feesAndExpensesSiteCoreKeys.raiseInvoiceBtn);
    return replaceHashWithValue(siteCoreLabel, {
      TOTALINVOICEAMOUNT: `${billingCurrencyName} ${state.totalBillingAmount.toFixed(2)}`,
    });
  }, [state.totalBillingAmount]);

  const getTotalCheckedAmount = (
    list: FeesAndExpenseRow[],
    key: keyof FeesAndExpenseRow,
  ) => {
    return list.reduce((acc, item) => {
      if (item.isChecked) {
        return acc + ((item[key] as number) ?? 0);
      }
      return acc;
    }, 0);
  };

  const onChangeSelect = (checked: boolean, item: FeesAndExpenseRow) => {
    const filterListIndex = state.filteredList.findIndex(
      (i) => i.id === item.id,
    );
    const originalListIndex = state.originalList.findIndex(
      (i) => i.id === item.id,
    );
    const updatedList = [...state.filteredList];
    const originalUpdatedList = [...state.originalList];
    originalUpdatedList[originalListIndex].isChecked = checked;
    updatedList[filterListIndex].isChecked = checked;
    const totalBillingAmount = getTotalCheckedAmount(
      updatedList,
      "totalAmount",
    );
    const totalTaxAmount = getTotalCheckedAmount(updatedList, "tax");

    setState((prevState) => ({
      ...prevState,
      filteredList: updatedList,
      originalList: originalUpdatedList,
      totalBillingAmount: totalBillingAmount,
      totalTax: totalTaxAmount,
    }));
  };

  const columns: Column[] = [
    {
      id: ProgramFessAndExpensesColumnKey.feesOrExpenseName,
      label: getLabel(feesAndExpensesSiteCoreKeys.feeExpenseName),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiLink
          onClick={() => handleOnViewClick(row as unknown as FeesAndExpenseRow)}
        >
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[ProgramFessAndExpensesColumnKey.feesOrExpenseName]}
            </MuiTypography>
          </MuiBox>
        </MuiLink>
      ),
    },
    {
      id: ProgramFessAndExpensesColumnKey.transactionDate,
      label: getLabel(feesAndExpensesSiteCoreKeys.transactionDate),
      isSortVisible: true,
    },
    {
      id: ProgramFessAndExpensesColumnKey.type,
      label: getLabel(feesAndExpensesSiteCoreKeys.itemType),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: ProgramFessAndExpensesColumnKey.category,
      label: getLabel(feesAndExpensesSiteCoreKeys.itemCategory),
      minWidth: 200,
      isSortVisible: true,
    },
    {
      id: ProgramFessAndExpensesColumnKey.feesOrExpenseType,
      label: getLabel(feesAndExpensesSiteCoreKeys.feeExpenseType),
      isSortVisible: true,
    },

    {
      id: ProgramFessAndExpensesColumnKey.statusId,
      label: getLabel(feesAndExpensesSiteCoreKeys.itemStatus),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge
          status={row.statusId}
          label={getStatusValueByKeyOfDropdown(row.statusId)}
        />
      ),
    },
    {
      id: ProgramFessAndExpensesColumnKey.unitRate,
      label: getLabel(feesAndExpensesSiteCoreKeys.unitRate),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProgramFessAndExpensesColumnKey.unitRate],
          row.currencyName,
        );
      },
    },
    {
      id: ProgramFessAndExpensesColumnKey.quantity,
      label: getLabel(feesAndExpensesSiteCoreKeys.quantity),
      isSortVisible: true,
    },
    {
      id: ProgramFessAndExpensesColumnKey.feesOrExpenseAmount,
      label: getLabel(feesAndExpensesSiteCoreKeys.amount),
      isSortVisible: true,
      render: (row: RowProps) => {
        return getAmountWithCurrency(
          row[ProgramFessAndExpensesColumnKey.feesOrExpenseAmount],
          row.currencyName,
        );
      },
    },

    {
      id: ProgramFessAndExpensesColumnKey.billingAmount,
      label: getLabel(feesAndExpensesSiteCoreKeys.billingAmount),
      isSortVisible: true,
      render: (row: RowProps) => {
        const currency = billingCurrencyName;
        return getAmountWithCurrency(
          row[ProgramFessAndExpensesColumnKey.billingAmount],
          currency,
        );
      },
    },
    {
      id: ProgramFessAndExpensesColumnKey.taxAmount,
      label: getLabel(feesAndExpensesSiteCoreKeys.taxAmount),
      isSortVisible: true,
      render: (row: RowProps) => {
        const currency = billingCurrencyName;
        return getAmountWithCurrency(
          row[ProgramFessAndExpensesColumnKey.taxAmount],
          currency,
        );
      },
    },
    {
      id: ProgramFessAndExpensesColumnKey.totalInvoiceAmount,
      label: getLabel(feesAndExpensesSiteCoreKeys.totalBillingAmount),
      isSortVisible: true,
      render: (row: RowProps) => {
        const currency = billingCurrencyName;
        return getAmountWithCurrency(
          row[ProgramFessAndExpensesColumnKey.totalInvoiceAmount],
          currency,
        );
      },
    },
    {
      id: ProgramFessAndExpensesColumnKey.comment,
      label: getLabel(feesAndExpensesSiteCoreKeys.comments),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          {isNotEmpty(row?.[ProgramFessAndExpensesColumnKey.comment]) ? (
            <MuiNotesToolTip
              message={row?.[ProgramFessAndExpensesColumnKey.comment]}
            />
          ) : (
            EMPTY_VALUE_DASH
          )}
        </MuiBox>
      ),
    },
  ];

  if (isEditWithPermission && isEditProgram && !isProgramTagWithProject) {
    columns.unshift({
      id: ProgramFessAndExpensesColumnKey.isChecked,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel.selectAll} ${getLabel(feesAndExpensesSiteCoreKeys.feeExpenseName)}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) => onChangeSelectAll(checked)}
        />
      ),
      render: (row: RowProps) => {
        return (
          <MuiCheckbox
            disabled={row.statusId !== STATUS_BADGE_TYPE.unBilled}
            checked={row.isChecked ?? false}
            hideLabel={true}
            checkboxLabel={replaceHashWithValue(
              commonARIALabel.selectRowItemName,
              `${row.name}`,
            )}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              onChangeSelect(checked, row as FeesAndExpenseRow);
            }}
          />
        );
      },
    });
  }

  if (!isViewOnly && !isProgramTagWithProject) {
    columns.push({
      id: "action",
      label: getLabel(feesAndExpensesSiteCoreKeys.itemAction),
      render: (row: RowProps) => {
        if (
          row.statusId === STATUS_BADGE_TYPE.unBilled &&
          row.typeId !== ADD_PROGRAM_FEE_AND_EXPENSE_TYPES.PROGRAM_FEE
        ) {
          const actions = [
            {
              permissionKey: MODULE_PERMISSIONS.EDIT_FEE_AND_EXPENSES,
              create: () => ({
                onClick: () =>
                  onActionClick(row as ProgramExpenseData, TableActions.edit),
                dataTestId: "edit-program-fee-and-expense",
                label: commonLabels.edit,
              }),
            },
            {
              permissionKey: MODULE_PERMISSIONS.DELETE_FEE_AND_EXPENSES,
              create: () => ({
                onClick: () =>
                  onActionClick(row as ProgramExpenseData, TableActions.delete),
                dataTestId: "delete-program-fee-and-expense",
                label: commonLabels.delete,
              }),
            },
          ];

          const menuItems = buildActionMenuItems(
            row,
            actions,
            hasModulePermission,
          );

          return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
        }
        return <></>;
      },
    });
  }

  const SearchDropdownControl = ({
    name,
    list,
  }: {
    name: string;
    list: OptionsDataType[];
  }) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        options={list}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${name}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    ...(isEditWithPermission && isEditProgram && !isProgramTagWithProject
      ? [<></>]
      : []),
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.feesOrExpenseName}
      control={control}
      placeholder={commonLabels.search}
      key="1"
      requiredARIALabel={commonARIALabel.required}
    />,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={ProgramFessAndExpensesColumnKey.transactionDate}
      key={ProgramFessAndExpensesColumnKey.transactionDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,
    <SearchDropdownControl
      name={ProgramFessAndExpensesColumnKey.type}
      list={state.typeIdOptions}
      key="2"
    />,
    <SearchDropdownControl
      name={ProgramFessAndExpensesColumnKey.category}
      list={state.categoryOptions}
      key="3"
    />,
    <SearchDropdownControl
      name={ProgramFessAndExpensesColumnKey.feesOrExpenseType}
      list={state.feeOrExpenseTypeOptions}
      key="4"
    />,
    <SearchDropdownControl
      name={ProgramFessAndExpensesColumnKey.statusId}
      list={fetchDropdownValues(
        feesAndExpensesSiteCoreKeys.feesAndExpenses,
        feesAndExpensesSiteCoreKeys.itemStatusList,
        false,
      )}
      key="5"
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.unitRate}
      control={control}
      placeholder={commonLabels.search}
      key="6"
      requiredARIALabel={commonARIALabel.required}
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.quantity}
      control={control}
      placeholder={commonLabels.search}
      key="7"
      requiredARIALabel={commonARIALabel.required}
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.feesOrExpenseAmount}
      control={control}
      placeholder={commonLabels.search}
      key="8"
      requiredARIALabel={commonARIALabel.required}
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.billingAmount}
      control={control}
      placeholder={commonLabels.search}
      key="9"
      requiredARIALabel={commonARIALabel.required}
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.taxAmount}
      control={control}
      placeholder={commonLabels.search}
      key="10"
      requiredARIALabel={commonARIALabel.required}
    />,
    <SearchFormControl
      name={ProgramFessAndExpensesColumnKey.totalInvoiceAmount}
      control={control}
      placeholder={commonLabels.search}
      key="11"
      requiredARIALabel={commonARIALabel.required}
    />,
    <></>,
    <></>,
  ];

  const addButtonLabel = getLabel(
    feesAndExpensesSiteCoreKeys.addBillingItemBtn,
  );

  const handleRaiseInvoice = () => {
    if (
      isClientTypeNCBP &&
      (isEmpty(programDetail?.dossierNumber) ||
        isEmpty(programDetail?.actualClientId))
    ) {
      setShowDossierErrorModal(true);
      return;
    }
    setShowRaiseInvoiceModal(true);
  };

  const handleCloseDossierError = () => {
    setShowDossierErrorModal(false);
  };

  function handleViewInvoiceHistory(): void {
    setShowSuccess(false);
    setShowRaiseInvoiceModal(false);
    setShowInvoiceHistoryModal(true);
  }

  const { downloadInvoiceExcel } = useDownloadInvoiceToExcel();
  function handleDownloadInvoice(selectedInvoice: string): void {
    downloadInvoiceExcel(selectedInvoice, DELIVERABLE_FRAMEWORK_TYPE.PROGRAM);
  }

  const onRaiseInvoiceValuesChange = (params: {
    invoiceDate: string;
    paymentTerms: string;
    poNumber: string;
    localInvoiceNumber: string;
  }) => {
    setSelectedPONumber(params.poNumber);
    setInvoiceDate(params.invoiceDate);
    setPaymentTerms(params.paymentTerms);
    setLocalInvoiceNumber(params.localInvoiceNumber);
  };

  return (
    <>
      <MuiBox className="fees-and-expense-container">
        {isViewOnly ? (
          <MuiDoubleLabelHorizontal
            label={getLabel(feesAndExpensesSiteCoreKeys.invoiceAmount)}
            value={getTotalInvoiceAmount()}
            className="invoice-amount"
          />
        ) : (
          <MuiBox className="add-fee-expense-btns">
            {isAddButtonVisible && !isProgramTagWithProject ? (
              <MuiButtonTertiary
                startIcon={<AddCircleOutlineIcon fontSize="large" />}
                onClick={handleAddFeesAndExpense}
                disabled={isProgramStatusClosed}
              >
                {addButtonLabel}
              </MuiButtonTertiary>
            ) : null}
            {Boolean(state.totalBillingAmount) && (
              <PermissionWrapper
                permission={MODULE_PERMISSIONS.PROGRAM_ADD_INVOICE_GENERATION}
              >
                <MuiBox className="divider"></MuiBox>
                <MuiButtonPrimary onClick={handleRaiseInvoice}>
                  {raiseInvoiceLabel}
                </MuiButtonPrimary>
              </PermissionWrapper>
            )}
          </MuiBox>
        )}
      </MuiBox>
      <MuiTable
        ref={tableRef}
        pageId={pageId}
        isLastColumnSticky={!isViewOnly && !isProgramTagWithProject}
        tableHeaderForm={tableHeaderForm}
        columns={columns}
        tableResults={
          state.filteredList as unknown as Record<string, ReactNode>[]
        }
        noDataInApi={state.originalList.length === 0}
        emptyMessage={emptyMessageLabel}
        noResultMessage={commonLabels.searchCriteria}
        priorityResults={[]}
        isShowDashIfValueEmpty
      />
      {!isViewOnly && showRaiseInvoiceModal && (
        <RaiseInvoice
          onClose={() => {
            setShowRaiseInvoiceModal(false);
            resetRaiseInvoice();
          }}
          modalTitle={fetchComponentTitle(
            raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
          )}
          open={showRaiseInvoiceModal}
          invoiceFeesAndExpense={state.filteredList.filter(
            (item) => item.isChecked,
          )}
          totalBillingAmount={parseFloat(
            (state.totalBillingAmount - state.totalTax).toFixed(2),
          )}
          totalTaxAmount={parseFloat(state.totalTax.toFixed(2))}
          totalInvoiceAmount={parseFloat(state.totalBillingAmount.toFixed(2))}
          totalInvoiceCurrency={billingCurrencyName}
          onPrimaryButtonClick={handlePrimaryButtonClick}
          programId={programId}
          captureValues={onRaiseInvoiceValuesChange}
          poNumber={poNumber}
          isRaiseInvoiceError={isRaiseInvoiceError}
          raiseInvoiceError={raiseInvoiceError}
        />
      )}

      {!isViewOnly && showInvoiceHistoryModal && (
        <MuiModal
          hasLargeContent
          open={showInvoiceHistoryModal}
          onClose={() => setShowInvoiceHistoryModal(false)}
          title={fetchComponentTitle(
            invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          )}
          primaryButtonTitle={commonLabels.ok}
          onPrimaryButtonClick={handleInvoiceButtonClick}
        >
          <InvoiceHistory programId={programId} />
        </MuiModal>
      )}

      {showConfirmation && (
        <MuiModal
          open={showConfirmation}
          onClose={handleCancel}
          title={commonLabels.confirmation}
          primaryButtonTitle={commonLabels.yesProceed}
          onPrimaryButtonClick={handleConfirm}
          secondaryButtonTitle={commonLabels.no}
          onSecondaryButtonClick={handleCancel}
        >
          <MuiTypography>
            {fetchLabel(
              raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
              raiseInvoiceSiteCoreKeys.areYouSureYouWantToRaiseTheInvoice,
            )}
          </MuiTypography>
        </MuiModal>
      )}

      {showSuccess && (
        <MuiModal
          open={showSuccess}
          onClose={handleSuccessClose}
          title={fetchLabel(
            raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
            raiseInvoiceSiteCoreKeys.successPopupHeader,
          )}
          primaryButtonTitle={commonLabels.ok}
          onPrimaryButtonClick={handleSuccessClose}
          tertiaryButtonTitle={
            hasModulePermission(MODULE_PERMISSIONS.PROGRAM_VIEW_INVOICE_HISTORY)
              ? fetchLabel(
                  raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
                  raiseInvoiceSiteCoreKeys.viewInvoiceHistoryLinkText,
                )
              : ""
          }
          onTertiaryButtonClick={() => handleViewInvoiceHistory()}
          leftActionButtonClick={() => handleDownloadInvoice(invoiceId)}
          leftActionButtonTitle={commonLabels.downloadInvoice}
          leftActionButtonIcon={<DownloadIcon />}
        >
          <MuiTypography
            dangerouslySetInnerHTML={{
              __html: replaceHashWithValue(
                fetchLabel(
                  raiseInvoiceSiteCoreKeys.raiseInvoiceTitle,
                  raiseInvoiceSiteCoreKeys.successPopupDescriptionText,
                ),
                {
                  INVOICENUMBER: invoiceNumber,
                  TOTALINVOICEAMONT: `${billingCurrencyName} ${state.totalBillingAmount.toFixed(2)}`,
                },
              ),
            }}
          ></MuiTypography>
        </MuiModal>
      )}

      {showProgramFeeViewModal && (
        <ViewProgramFeeModal
          open={showProgramFeeViewModal}
          onClose={() => setShowProgramFeeViewModal(false)}
          programId={programId}
        />
      )}
      {showViewFeesAndExpenseModal && (
        <ViewFeeExpenseModal
          billingCurrencyName={billingCurrencyName}
          open={showViewFeesAndExpenseModal}
          programExpenseId={selectedProgramExpenseId}
          onClose={() => setShowViewFeesAndExpenseModal(false)}
        />
      )}

      {showDossierErrorModal && (
        <WarningConfirmationModal
          primaryButtonTitle={commonLabels.ok}
          show={showDossierErrorModal}
          onClose={handleCloseDossierError}
          showSecondaryButton={false}
          labelContent={getLabel(
            feesAndExpensesSiteCoreKeys.dossierNumberActualClientNotAvailableMessage,
          )}
          onPrimaryButtonClick={handleCloseDossierError}
        />
      )}
    </>
  );
};
